// Pure helpers for EventQuickRegisterService (specs/ai-agent-home-redesign US-20/US-21).
// They do in code what the fan does on the tickets page: pick one ticket for themselves and
// fill the attendee row, then build the same registerEvent payload as
// EventTicketsComponent.registerEvent() for that single self ticket.

// The free ticket a fan can take without any input, or null when the form is needed.
// Add-ons (optional extras) and NFT tickets are never picked; tickets that need shipping or
// have their own mandatory question are skipped; a mandatory general question blocks all.
// Virtual tickets come before live ones, the same default as the tickets page.
export function pickQuickRegisterTicket(tickets: any[], questions: any[]): any | null {
  const mandatory = (questions || []).filter(q => q?.is_mandatory == 1);
  if (mandatory.some(q => q.question_type === 'general')) return null;
  const usable = (tickets || []).filter(t =>
    t?.ticket_type === 'free' && t.isDeleted !== 1 && !hasItems(t.add_ons) && !t.token_id &&
    t.shipping_address != 1 && !mandatory.some(q => q.ticket_id == t.id));
  return usable.find(t => !t.live) || usable[0] || null;
}

export function questionnaireOn(stream: any): boolean {
  const settings = typeof stream?.settings_json === 'string' ? safeParse(stream.settings_json) : stream?.settings_json;
  return settings?.questionnaire == 1;
}

// The fan as the only attendee (first contact row of the tickets page, pre-filled from the profile).
export function attendeeFromUser(user: any): { first_name: string; last_name: string; email: string } {
  return {
    first_name: user?.user_first_name || '',
    last_name: user?.user_last_name || '',
    email: (user?.email || user?.user_email || '').toLowerCase(),
  };
}

// Mirrors EventTicketsComponent.registerEvent() for a free order of one ticket for the buyer.
// userId is the id getUserByEmail returned for the fan, as the tickets page uses.
export function buildQuickRegisterPayload(stream: any, ticket: any, user: any, userId: any, networkGroupId?: number, country = ''): any {
  const groupId = networkGroupId || stream.network_group_id || 0;
  return {
    paymentType: 'pay_per_view',
    payment_type: 'pay_per_view',
    organization_id: stream.organization_id || '',
    currency: ticketCurrency(ticket) || stream.currency_type || 'USD',
    amount: 0,
    stream_id: stream.id,
    streamInfo: {
      id: stream.id,
      stream_video_title: stream.stream_video_title,
      organization_id: stream.organization_id,
      amount: 0,
      currency: stream.currency_type,
      stream_amounts: stream.displayAmounts ?? stream.stream_amounts,
      publish_date_time: stream.publish_date_time,
      video_thumbnail_image: stream.video_thumbnail_image,
      video_banner_image: stream.video_banner_image,
      performer_id: stream.performer_id,
      user_first_name: stream.user_first_name,
      user_last_name: stream.user_last_name,
      user_display_name: stream.user_display_name,
      channel_name: stream.channel_name,
      number_of_creators: stream.number_of_creators,
      stream_status: stream.stream_status,
      currency_type: stream.currency_type,
      expected_end_date_time: stream.expected_end_date_time,
      slug: stream.slug,
      selected_type: stream.selected_type,
      network_group_id: groupId,
    },
    tickets: [{
      id: ticket.id,
      // The tickets page sends 1 for the buyer's own ticket and leaves it out on live tickets.
      for_self: ticket.live == 1 ? undefined : 1,
      limited_tickets: ticket.limited_tickets,
      name: ticket.title,
      sessions: ticket.sessions,
      networking: ticket.networking,
      live: ticket.live,
      live_qa: ticket.live_qa,
      expo: ticket.expo,
      questions: [],
      add_ons: undefined,
      // Re-priced per attendee on the tickets page; always 0 for a free ticket.
      price: 0,
      amounts: parseAmounts(ticket.amounts),
      discountInfo: {},
      discountTickets: '',
      user: { id: userId, ...attendeeFromUser(user) },
    }],
    country,
    timezone_offset: new Date().getTimezoneOffset(),
    network_group_id: groupId,
  };
}

// The tickets page uses the selected ticket's currency for the order.
function ticketCurrency(ticket: any): string {
  const amounts = parseAmounts(ticket.amounts);
  return Array.isArray(amounts) ? amounts[0]?.currency || '' : '';
}

// Tickets return amounts as a JSON string; the tickets page parses it before sending.
function parseAmounts(amounts: any): any {
  if (!amounts) return 0;
  return typeof amounts === 'string' ? safeParse(amounts) ?? amounts : amounts;
}

function hasItems(value: any): boolean {
  if (!value) return false;
  const list = typeof value === 'string' ? safeParse(value) : value;
  return Array.isArray(list) ? list.length > 0 : !!list;
}

export function safeParse(value: string): any {
  try { return JSON.parse(value); } catch { return null; }
}
